import { Row } from '@tanstack/react-table';

import { useScopedTranslate } from '@locale/translate';

import { AppliedFilterData } from './filters';
import { DataItemType, ColumnType } from './types';

export const sortByReferenceArray = (
  arrayToSort: Array<string>,
  arrayToReference: Array<string>
) => {
  return arrayToSort.sort((x, y) => {
    const indexX = arrayToReference.indexOf(x);
    const indexY = arrayToReference.indexOf(y);

    // Handle cases where elements might not be in arrayToReference
    if (indexX === -1 && indexY === -1) return 0;
    if (indexX === -1) return 1;
    if (indexY === -1) return -1;

    return indexX - indexY;
  });
};

const localeCompare = (valueA: string, valueB: string) => {
  return String(valueA).toLowerCase().localeCompare(String(valueB).toLowerCase(), 'is');
};

const numberCompare = (valueA: number, valueB: number) => {
  return valueA - valueB;
};

const dateSort = ({ rowA, rowB, columnId }: SortProps) => {
  const a = rowA.original[columnId];
  const b = rowB.original[columnId];
  if (b < a) {
    return -1;
  }
  if (b > a) {
    return 1;
  }
  return 0;
};

type SortProps = {
  rowA: Row<DataItemType>;
  rowB: Row<DataItemType>;
  columnId: DataItemType['id'];
  appliedFilter?: AppliedFilterData;
};

const numberSort = ({ rowA, rowB, columnId }: SortProps) => {
  return numberCompare(rowA.original[columnId], rowB.original[columnId]);
};

const textSort = ({ rowA, rowB, columnId }: SortProps) => {
  return localeCompare(rowA.original[columnId], rowB.original[columnId]);
};

const singleSelectSort = ({ rowA, rowB, columnId }: SortProps) => {
  if (typeof rowA.original[columnId] === 'string') {
    return textSort({ rowA, rowB, columnId });
  }
  return localeCompare(rowA.original[columnId].label, rowB.original[columnId].label);
};

const personSort = ({ rowA, rowB, columnId }: SortProps) => {
  return localeCompare(rowA.original[columnId]?.name, rowB.original[columnId]?.name);
};

const issuesSort = ({ rowA, rowB, columnId, appliedFilter }: SortProps) => {
  const { warnings: warningsA, errors: errorsA } = rowA.original[columnId];
  const { warnings: warningsB, errors: errorsB } = rowB.original[columnId];

  const filterValues = appliedFilter?.value ?? [];
  const hasErrorFilter = filterValues.includes('error');
  const hasWarningFilter = filterValues.includes('warning');

  let issuesA = 0;
  let issuesB = 0;

  if (hasWarningFilter && !hasErrorFilter) {
    issuesA = warningsA.length;
    issuesB = warningsB.length;
  } else if (hasErrorFilter && !hasWarningFilter) {
    issuesA = errorsA.length;
    issuesB = errorsB.length;
  } else {
    issuesA = warningsA.length + errorsA.length;
    issuesB = warningsB.length + errorsB.length;
  }

  return numberCompare(issuesA, issuesB);
};

export const sortingMap: Record<ColumnType, (props: SortProps) => number> = {
  email: textSort,
  text: textSort,
  paragraph: textSort,
  phone_number: textSort,
  progress: numberSort,
  number: numberSort,
  decimal: numberSort,
  single_select: singleSelectSort,
  multi_select: textSort,
  user: personSort,
  date: dateSort,
  datetime: dateSort,
  file: numberSort,
  file_multi: numberSort,
  boolean: numberSort,
  issues: issuesSort,
  custom: textSort,
  array: numberSort,
  array_of_objects: numberSort,
};

type SorterProps = SortProps & {
  activeColumnTypeMap: Record<DataItemType['id'], DataItemType['type']>;
  allFilters: Array<AppliedFilterData>;
};

export const sorter = ({ rowA, rowB, columnId, activeColumnTypeMap, allFilters }: SorterProps) => {
  const columnType: ColumnType = activeColumnTypeMap[columnId];
  const appliedFilter = allFilters.find((filter) => filter.category === columnType);
  const sortingFunction = sortingMap[columnType || 'text'] || textSort;
  return sortingFunction({ rowA, rowB, columnId, appliedFilter });
};

export const nonSortableTypes: Array<ColumnType> = ['file', 'file_multi', 'multi_select'];

export const useSortLocale = (): Record<ColumnType, { asc: string; desc: string }> => {
  const { t } = useScopedTranslate(sortLocale);

  return {
    text: {
      asc: t('alphabeticalAsc'),
      desc: t('alphabeticalDesc'),
    },
    paragraph: {
      asc: t('alphabeticalAsc'),
      desc: t('alphabeticalDesc'),
    },
    single_select: {
      asc: t('alphabeticalAsc'),
      desc: t('alphabeticalDesc'),
    },
    multi_select: {
      asc: t('alphabeticalAsc'),
      desc: t('alphabeticalDesc'),
    },
    date: {
      asc: t('dateAsc'),
      desc: t('dateDesc'),
    },
    datetime: {
      asc: t('dateAsc'),
      desc: t('dateDesc'),
    },
    email: {
      asc: t('alphabeticalAsc'),
      desc: t('alphabeticalDesc'),
    },
    user: {
      asc: t('alphabeticalAsc'),
      desc: t('alphabeticalDesc'),
    },
    phone_number: {
      asc: t('numericalAsc'),
      desc: t('numericalDesc'),
    },
    progress: {
      asc: t('progressAsc'),
      desc: t('progressDesc'),
    },
    number: {
      asc: t('numericalAsc'),
      desc: t('numericalDesc'),
    },
    decimal: {
      asc: t('numericalAsc'),
      desc: t('numericalDesc'),
    },
    file: {
      asc: t('numericalAsc'),
      desc: t('numericalDesc'),
    },
    file_multi: {
      asc: t('numericalAsc'),
      desc: t('numericalDesc'),
    },
    boolean: {
      asc: t('booleanAsc'),
      desc: t('booleanDesc'),
    },
    issues: {
      asc: t('issuesAsc'),
      desc: t('issuesDesc'),
    },
    custom: {
      asc: t('alphabeticalAsc'),
      desc: t('alphabeticalDesc'),
    },
    array: {
      asc: t('numericalAsc'),
      desc: t('numericalDesc'),
    },
    array_of_objects: {
      asc: t('numericalAsc'),
      desc: t('numericalDesc'),
    },
  };
};

const sortLocale = {
  en: {
    alphabeticalAsc: 'Sort A → Z',
    alphabeticalDesc: 'Sort Z → A',
    dateAsc: 'Sort Newest → Oldest',
    dateDesc: 'Sort Oldest → Newest',
    numericalAsc: 'Sort 1 → 9',
    numericalDesc: 'Sort 9 → 1',
    progressAsc: 'Sort 0% → 100%',
    progressDesc: 'Sort 100% → 0%',
    booleanAsc: 'False → True',
    booleanDesc: 'True → False',
    issuesAsc: 'Lowest → Highest',
    issuesDesc: 'Highest → Lowest',
  },
  is: {
    alphabeticalAsc: 'Raða A → Ö',
    alphabeticalDesc: 'Raða Ö → A',
    dateAsc: 'Raða Nýjast → Elst',
    dateDesc: 'Raða Elst → Nýjast',
    numericalAsc: 'Raða 1 → 9',
    numericalDesc: 'Raða 9 → 1',
    progressAsc: 'Raða 0% → 100%',
    progressDesc: 'Raða 100% → 0%',
    booleanAsc: 'Nei → Já',
    booleanDesc: 'Já → Nei',
    issuesAsc: 'Fæst → Flest',
    issuesDesc: 'Flest → Fæst',
  },
};
